import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi, BACKEND } from "@/lib/api";
import { Btn } from "@/lib/ui";
import { ImageIcon, UploadCloud } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";

export function mediaUrl(id) {
  return `${BACKEND}/api/public/media/${id}/raw`;
}

const MEDIA_RE = /\/api\/public\/media\/([^/]+)\/raw/;
const _blobCache = new Map(); // mediaId -> object URL (persists for app lifetime; bounded by distinct media)

// Renders an image for the ADMIN UI. If the src is a public Media-Library URL,
// it fetches the authenticated blob (so unpublished images still preview) and
// caches it per id; otherwise it renders the external URL directly. The stored
// value stays the public URL so the external website can render it once published.
export function MediaImg({ src, alt = "", className, testid, fallback = null }) {
  const api = useApi();
  const id = useMemo(() => { const m = (src || "").match(MEDIA_RE); return m ? m[1] : null; }, [src]);
  const [url, setUrl] = useState(id ? _blobCache.get(id) || null : src || null);
  useEffect(() => {
    let cancelled = false;
    if (id) {
      if (_blobCache.has(id)) { setUrl(_blobCache.get(id)); return; }
      api.blob(`/media/${id}/raw`)
        .then((u) => { if (cancelled) { URL.revokeObjectURL(u); return; } _blobCache.set(id, u); setUrl(u); })
        .catch(() => { if (!cancelled) setUrl(null); });
    } else { setUrl(src || null); }
    return () => { cancelled = true; };
  }, [id, src]); // eslint-disable-line
  if (!url) return fallback;
  return <img src={url} alt={alt} className={className} data-testid={testid} />;
}

export function MediaPicker({ onPick, folder = "CMS", label = "Media Library" }) {
  const api = useApi();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data: media = [] } = useQuery({ queryKey: ["media", "image"], queryFn: () => api.get("/media?kind=image"), enabled: open });
  const upload = useMutation({
    mutationFn: (file) => { const f = new FormData(); f.append("file", file); f.append("folder", folder); return api.upload("/media", f); },
    onSuccess: (m) => { toast.success("Image uploaded"); qc.invalidateQueries({ queryKey: ["media", "image"] }); onPick(mediaUrl(m.id)); setOpen(false); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Btn variant="outline" size="sm" type="button" data-testid="media-picker-open" className="shrink-0 whitespace-nowrap"><ImageIcon className="h-4 w-4" /> {label}</Btn>
      </DialogTrigger>
      <DialogContent className="bg-surface border-border max-w-2xl">
        <DialogHeader>
          <DialogTitle>Choose an image</DialogTitle>
          <DialogDescription>Upload a new image or pick an existing one from the Media Library.</DialogDescription>
        </DialogHeader>
        <label className="flex items-center gap-2 text-sm text-primary cursor-pointer hover:underline w-max" data-testid="media-upload-label">
          <UploadCloud className="h-4 w-4" /> Upload a new image
          <input type="file" accept="image/*" className="hidden" data-testid="media-upload-input"
            onChange={(e) => e.target.files[0] && upload.mutate(e.target.files[0])} />
        </label>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-80 overflow-y-auto" data-testid="media-grid">
          {media.length === 0 && <p className="text-xs text-muted-foreground col-span-full py-6 text-center">No images yet. Upload one above.</p>}
          {media.map((m) => {
            const url = mediaUrl(m.id);
            return (
              <button key={m.id} type="button" data-testid={`media-item-${m.id}`}
                onClick={() => { onPick(url); setOpen(false); }}
                className="aspect-square rounded-md overflow-hidden border border-border hover:border-primary transition-colors">
                <MediaImg src={url} alt={m.alt_text || m.filename} className="h-full w-full object-cover" />
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
